// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import { change } from 'redux-form'
import { warning } from 'react-notification-system-redux'
import { UNCAUGHT_ERROR } from 'errors'
import { postNewRadioFavorite } from 'helpers/api'
import canCreateRadioFavoriteOnPath from 'helpers/canCreateRadioFavoriteOnPath'
import { radioDataForForm } from '../helpers'

import {
  castToFloat,
  getNestedByString,
} from 'helpers/reduxFormFunctions'

const createRadioFavorite = (sourceForm, nameRoot) => async (dispatch, getState) => {
  const state = getState()
  const userId = state.user.get('userId')
  const { values } = state.form[sourceForm]
  const projectTypeId = state.projectSummary.getIn(['projectType', 'projectTypeId'])

  if (!canCreateRadioFavoriteOnPath(projectTypeId, values.band)) {
    return
  }

  const {
    acmCoordinationAdvantageFlag,
    atpc,
    coordinatedPower,
    engineeringReference,
    modulations,
    radioId,
  } = getNestedByString(values, nameRoot)

  try {
    // since power is a required field for radio favorites,
    // check that all enabled modulations have a valid power before sending the request to the API.
    // API does not give a user friendly error for this scenario.
    let enabledModulationMissingPower = null

    const radioModulations = modulations.reduce((a, d, i) => {
      if (d.enabled && castToFloat(d.power) === null) {
        enabledModulationMissingPower = d.modulationSchemeDescription
      }

      if (d.enabled) {
        const enabledPower = castToFloat(d.power)
        const radioModulationId = castToFloat(d.radioModulationId)

        a.push({
          enabledPower,
          radioModulationId,
        })
      }

      return a
    }, [])

    if (enabledModulationMissingPower) {
      throw new Error(`Enabled modulation ${enabledModulationMissingPower} must have a power assigned to create this radio favorite.`)
    }

    const pathId = state.pathDetails.get('pathId')
    const body = {
      radioId,
      radioModulations,
      createUserId: userId,
      ...(pathId ? { pathId } : {}),
    }

    const { entity } = await postNewRadioFavorite(body)

    // assemble a mock of radioModulationConfigurations for use radioDataForForm.
    // radioModulationConfigurations is defined on segment end.
    // Since the Radio API doesn't know anything about that,
    // take the current state of the modulations in redux-form state
    // and reduce that to match what it would be if it were
    // radioModulationConfigurations from the API.

    const radioModulationConfigurations = entity.modulations.reduce((a, d, i) => {
      if (!d.enabled) {
        return a
      }

      a.push({
        engineeringReferenceFlag: d.radioModulationId === +engineeringReference,
        // enabled power is based on the defined power in the radio favorite modulation.
        power: d.enabledPower,
        radioModulationId: d.radioModulationId,
      })

      return a
    }, [])

    const se = {
      acmCoordinationAdvantageFlag,
      atpc,
      coordinatedPower,
      radioModulationConfigurations,
      radio: { ...entity },
    }

    dispatch(change(sourceForm, nameRoot, radioDataForForm(se)))
  } catch (err) {
    console.warn(err)
    dispatch(warning({
      autoDismiss: 3,
      message: err.message || UNCAUGHT_ERROR,
      title: 'Error Creating Radio Favorite:',
    }))
  }
}

export default createRadioFavorite
